// Locale persistence + lookup. Mirrors utils/theme.ts: a localStorage-backed
// preference reflected onto the <html> element, resolved once at boot. Adds a
// `?lang=` URL reflection for shareable links + hreflang, and a `t()` lookup
// over the compiled message catalog.
//
// Code-splitting: only the EN catalog is in the main bundle. ES and PT-BR are
// lazy-loaded via dynamic import() on first use — keeps the initial JS under
// the 30 kB gzip budget. The catalog is always loaded before `t()` is called
// (initLocale awaits the import; setLocale awaits before dispatching the
// re-render event).
//
// WAF-safe: `lang` is a strict 3-value enum. Anything read from the URL or
// storage is clamped to that enum (unknown values are ignored, never echoed),
// and only canonical values are ever written to the URL — so no freeform text
// reaches the address bar to trip Cloud Armor's 941xxx/942xxx rules.

import { en, type Locale, type Messages, type MsgKey } from './messages';

export type { Locale, MsgKey } from './messages';
export { LOCALES } from './messages';

const KEY = 'locale';
export const LOCALE_EVENT = 'locale-changed';

let current: Locale = 'en';
let catalog: Messages = en;

function clamp(v: string | null | undefined): Locale | null {
  return v === 'en' || v === 'es' || v === 'pt-BR' ? v : null;
}

function fromUrl(): Locale | null {
  try {
    return clamp(new URLSearchParams(location.search).get('lang'));
  } catch {
    return null;
  }
}

function fromStorage(): Locale | null {
  try {
    return clamp(localStorage.getItem(KEY));
  } catch {
    // private-mode storage failure — no preference
    return null;
  }
}

function fromNavigator(): Locale {
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const t = (tag ?? '').toLowerCase();
    if (t.startsWith('pt')) return 'pt-BR';
    if (t.startsWith('es')) return 'es';
    if (t.startsWith('en')) return 'en';
  }
  return 'en';
}

// Resolution precedence: an explicit ?lang= (shared link) wins, then the stored
// choice, then the browser's languages, then English.
export function getStoredLocale(): Locale {
  return fromUrl() ?? fromStorage() ?? fromNavigator();
}

export function getLocale(): Locale {
  return current;
}

function apply(l: Locale): void {
  current = l;
  document.documentElement.lang = l;
  document.documentElement.setAttribute('data-locale', l);
}

function persist(l: Locale): void {
  try {
    localStorage.setItem(KEY, l);
  } catch {
    // ignore — preference just won't survive the session
  }
}

// Reflect the active locale into ?lang= for shareable links and hreflang.
// English is the x-default, so it carries no param (clean canonical URL).
function syncUrl(l: Locale): void {
  try {
    const url = new URL(location.href);
    if (l === 'en') url.searchParams.delete('lang');
    else url.searchParams.set('lang', l);
    history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  } catch {
    // ignore — URL reflection is a nicety, not load-bearing
  }
}

async function loadCatalog(l: Locale): Promise<Messages> {
  if (l === 'es') return (await import('./messages-es')).default;
  if (l === 'pt-BR') return (await import('./messages-pt-BR')).default;
  return en;
}

// Boot: resolve and apply the locale before the first route renders. Awaits the
// catalog import so `t()` is ready by the time the router mounts.
export async function initLocale(): Promise<void> {
  const l = getStoredLocale();
  catalog = await loadCatalog(l);
  apply(l);
  persist(l);
}

export async function setLocale(l: Locale): Promise<void> {
  if (l === current) return;
  catalog = await loadCatalog(l);
  apply(l);
  persist(l);
  syncUrl(l);
  window.dispatchEvent(new CustomEvent(LOCALE_EVENT, { detail: l }));
}

export function t(key: MsgKey): string {
  return catalog[key];
}
